import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import Constants from "expo-constants";
import {
  displayErrorMessage,
  FlexGrid,
  Message,
  Panel,
  Text,
  theme,
} from "@clearabee/ui-library-native";
import { getItemDescendants, useCatalogue } from "@clearabee/ui-sdk";
import { Input } from "components/Core/Input/Input";
import { useRouter } from "lib/hooks/useRouter";
import { Button } from "components/Core/Button/Button";
import { styles, ids } from "./BulkSelectProducts.styles";
import { ProductList } from "./ProductList";
import { ErrorPage } from "components/ErrorPage/ErrorPage";
import { Layout } from "components/Layout/Layout";
import { Form, useForm } from "lib/hooks/useForm";
import { z } from "zod";
import { getValidation } from "./BulkSelectProducts.validation";
import { sortByPrice } from "lib/utils";
import { useAnalytics } from "lib/hooks/useAnalytics";

interface BulkSelectProductsProps {
  categorySku: string;
}

export const validation = z.object({
  products: z.object({
    sku: z.number(),
  }),
});

export const BulkSelectProducts = ({
  categorySku,
}: BulkSelectProductsProps): React.ReactElement => {
  const [filter, setFilter] = useState("");
  const [translate] = useTranslation();
  const { navigate } = useRouter();
  const { defaultCatalogue } = useCatalogue();
  const { ecommerce } = useAnalytics();
  const service = defaultCatalogue.items.find(({ sku }) => sku === categorySku);
  const productsRef = useRef(
    defaultCatalogue.items.filter(({ parentSku }) => parentSku === categorySku),
  );

  const serviceProducts = getItemDescendants(
    categorySku,
    defaultCatalogue.items,
  );
  const filteredProducts = sortByPrice(serviceProducts).filter((product) => {
    const found = [product.title, product.sku].some((string) =>
      string?.toLowerCase().includes(filter.trim().toLowerCase()),
    );

    return !filter || (filter && found);
  });

  const form = useForm({
    defaultValues: { products: {} },
    reValidateMode: "onChange",
    validationSchema: getValidation(productsRef.current),
    onSubmit: (values) => {
      const selectedItems = Object.entries(values.products).filter(
        ([, qty]) => !!qty,
      );
      navigate({
        routeName: "BulkAdd",
        params: {
          categorySku: categorySku,
          products: selectedItems.map((entry) => entry.join(",")).join(","),
        },
      });
    },
  });
  const {
    submitForm,
    formState: { errors, isValid },
  } = form;

  useEffect(() => {
    ecommerce.viewItemList(
      serviceProducts,
      `${categorySku} Product List`,
      `${categorySku}_product_list`,
    );
  }, []);

  useEffect(() => {
    if (!!errors && isValid) {
      form.clearErrors();
    }
  }, [isValid]);

  if (!service) return <ErrorPage />;

  return (
    <Layout
      title={translate("hwa.screens.selectProduct.title")}
      backTo={{ routeName: "Book" }}
      contentWidth="xlarge"
    >
      <View
        style={{ maxWidth: theme.screens.large, margin: "auto", width: "100%" }}
      >
        <Panel shadow={Platform.OS === "web"}>
          <FlexGrid rowSpacing={null} style={{ alignItems: "center" }}>
            <FlexGrid.Cell base="100%" medium="35%">
              <Input.Text
                name="search"
                value={filter}
                onChange={(value) => setFilter(value)}
                placeholder={translate("hwa.screens.selectProduct.filterItems")}
              />
            </FlexGrid.Cell>
            <FlexGrid.Cell
              base="100%"
              medium="65%"
              style={{ justifyContent: "flex-end" }}
            >
              <View style={styles.nextWrap} dataSet={{ media: ids.nextWrap }}>
                <Text
                  fontSize="small"
                  styles={styles.description}
                  dataSet={{ media: ids.description }}
                >
                  {translate("hwa.screens.selectProduct.bulkAddDescription")}
                </Text>
                <Button
                  color="warning"
                  size="medium"
                  styles={styles.nextButton}
                  onClick={() => {
                    submitForm();
                  }}
                >
                  {translate("hwa.screens.selectProduct.nextStep")}
                </Button>
              </View>
            </FlexGrid.Cell>
          </FlexGrid>
        </Panel>
        {!!Object.keys(errors).length && (
          <View
            style={{
              marginTop: theme.spacing.large,
              alignItems: "center",
            }}
          >
            {displayErrorMessage(Object.values(errors), (props) => (
              <Message
                {...props}
                type="error"
                color="light"
                background
                styles={{
                  margin: "auto",
                  backgroundColor: theme.colors.negative.base,
                }}
              />
            ))}
          </View>
        )}
        {!filteredProducts.length && (
          <View
            style={{
              marginTop: theme.spacing.large,
              alignItems: "center",
            }}
          >
            <Message
              type="error"
              color="light"
              background
              styles={{
                margin: "auto",
                backgroundColor: theme.colors.negative.base,
              }}
            >
              {translate("hwa.screens.selectProduct.searchError", {
                searchTerm: filter,
              })}
            </Message>
          </View>
        )}
        <Form state={form}>
          <ProductList products={filteredProducts} />
        </Form>
      </View>
    </Layout>
  );
};
